import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { PageHero, Section } from "../../components/Page";
import { SubNav, CONTACT_NAV } from "../../components/SubNav";
import { useApi } from "../../hooks/useApi";
import { Calendar, MapPin, Video, ArrowLeft } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { useLang } from "../../i18n/LanguageContext";
import { ShareBanner } from "../../components/ShareBanner";
import { TeamupCalendar } from "../../components/TeamupCalendar";
import { AddToCalendar } from "../../components/AddToCalendar";
import { timeRange } from "../../lib/dates";
import { EventFlyer } from "../../components/EventFlyer";
import { safeHref } from "../../lib/safeHref";

export function EventsPublic() {
  const { data } = useApi("/events?upcoming=true");
  const { data: banners } = useApi("/banners/active");
  const { t } = useLang();
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || !banners) return;
    const el = document.getElementById(hash.slice(1));
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-4", "ring-gold"); }
  }, [hash, banners]);
  return (
    <div data-testid="events-page">
      <PageHero eyebrow="Gatherings" title="Events & Festivals"
        subtitle="Monthly meetings, Odwira and Ohum festivals, inaugurations and social events."
        image="https://images.unsplash.com/photo-1634074967416-dd24d702c93c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      {banners?.length > 0 && (
        <Section>
          <p className="eyebrow text-gold">{t("Important Announcement")}</p>
          <h2 className="mt-1 font-serif-display text-3xl font-bold text-crimson">{t("Announcements")}</h2>
          <div className="mt-5 grid gap-6 md:grid-cols-2" data-testid="events-announcements">
            {banners.map((b) => (
              <div key={b.id} id={`announcement-${b.id}`} className="announce-banner relative overflow-hidden rounded-2xl p-6 text-parchment transition-shadow" data-testid={`announcement-card-${b.id}`}>
                <div className="announce-glow" />
                <div className="relative">
                  <span className="announce-ribbon">{t(b.label || "Important Announcement")}</span>
                  <h3 className="mt-3 font-serif-display text-3xl font-bold text-gold-bright" data-testid={`announcement-title-${b.id}`}>{b.title}</h3>
                  <p className="mt-2 text-parchment/90">{b.message}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-sm font-semibold text-gold-bright">
                    <span className="inline-flex items-center gap-1"><Calendar size={15} /> {b.date}</span>
                    {b.location && <span className="inline-flex items-center gap-1"><MapPin size={15} /> {b.location}</span>}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {b.event_id && <Link to={`/events/${b.event_id}`} className="rounded-full bg-gold px-4 py-1.5 text-sm font-bold text-crimson hover:bg-gold-bright" data-testid={`announcement-event-${b.id}`}>{t("Event details")}</Link>}
                    <ShareBanner title={b.title} message={b.message} path={`/events#announcement-${b.id}`} testId={`announcement-share-${b.id}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
      <Section>
        <TeamupCalendar scope="public" title="Full association calendar" />
      </Section>
      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {(data || []).map((e) => (
            <div key={e.id} className="card-hover glass flex flex-col rounded-2xl p-6" data-testid={`event-card-${e.id}`}>
              <EventFlyer event={e} compact className="mb-4" />
              <Link to={`/events/${e.id}`} className="flex-1" data-testid={`event-link-${e.id}`}>
                <span className="eyebrow text-gold">{e.type}</span>
                {e.cancelled && <span className="ml-2 rounded-full bg-crimson px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-parchment" data-testid={`event-cancelled-${e.id}`}>{t("Cancelled")}</span>}
                <h3 className="mt-2 font-serif-display text-2xl font-bold text-crimson">{e.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-foreground/70">{e.description}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-palm"><Calendar size={15} /> {e.date}{timeRange(e)}</div>
                <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={15} /> {e.location}</div>
              </Link>
              <div className="mt-4 space-y-3 border-t border-gold/20 pt-3">
                <ShareBanner title={e.title} message={`${e.date}${e.location ? ` · ${e.location}` : ""}`} path={`/events/${e.id}`} variant="light" testId={`event-share-${e.id}`} />
                <AddToCalendar event={e} compact />
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function EventDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { data: e } = useApi(`/events/${id}`, [id]);
  if (!e) return <Section><p>{t("Loading…")}</p></Section>;
  return (
    <div data-testid="event-detail-page">
      <PageHero eyebrow={e.type} title={e.title} subtitle={e.description}
        image="https://images.unsplash.com/photo-1660675133902-acd1b057f75d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <Section>
        <Link to="/events" className="mb-6 inline-flex items-center gap-1 text-sm text-crimson"><ArrowLeft size={15} /> {t("Back to events")}</Link>
        <EventFlyer event={e} className="mb-8 max-w-2xl" testId="event-detail-flyer" />
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-serif-display text-2xl font-bold text-crimson">{t("Agenda")}</h2>
            <ol className="mt-4 space-y-2">
              {(e.agenda || []).map((a, i) => (
                <li key={i} className="flex gap-3 rounded-lg bg-parchment-card p-3 border border-gold/20">
                  <span className="font-mono-accent text-gold">{String(i + 1).padStart(2, "0")}</span>{a}
                </li>
              ))}
            </ol>
          </div>
          <aside className="space-y-3">
            <div className="glass rounded-2xl p-6 space-y-3 text-sm">
              <div className="flex items-center gap-2"><Calendar size={16} className="text-gold" /> {e.date}{timeRange(e)}</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="text-gold" /> {e.location}</div>
              {safeHref(e.virtual_link) && <a href={safeHref(e.virtual_link)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-palm"><Video size={16} /> {t("Join virtually")}</a>}
              <p className="text-muted-foreground">{e.rsvp_count} {t("going")}</p>
              <div className="border-t border-gold/20 pt-3 space-y-3"><ShareBanner title={e.title} message={`${e.date}${e.location ? ` · ${e.location}` : ""}`} path={`/events/${e.id}`} variant="light" testId="event-detail-share" /><AddToCalendar event={e} testId="event-detail-add-to-calendar" /></div>
            </div>
            <Button asChild className="w-full bg-crimson hover:bg-crimson-hover"><Link to="/portal/events">{t("RSVP in portal")}</Link></Button>
          </aside>
        </div>
      </Section>
    </div>
  );
}

export function FAQ() {
  const { t } = useLang();
  const { data: sched } = useApi("/dues/schedule");
  const { data: pay } = useApi("/settings/payment");
  const reg = Number(sched?.registration_fee ?? 50).toFixed(0);
  const monthly = Number(sched?.monthly_dues ?? 20).toFixed(0);
  const payAnswer = pay?.guidance?.text
    ? `${pay.online_payments ? "Yes — card payments are available in the member portal. " : "Online card payments are coming soon. "}${pay.guidance.text} After paying, submit an offline payment claim in the portal so an officer can confirm it and your balance updates.`
    : "Please see the Dues page in the member portal for current payment methods, then submit an offline payment claim so an officer can confirm it.";
  const faqs = [
    ["How do I become a member?", "Complete the online application. The General Secretary will contact you for vetting and your application is reported at the next Executive Board meeting. Once approved you receive an email to set your password and gain full member access; the registration fee is then recorded on your account."],
    ["What are the dues?", t("A one-time ${reg} registration fee plus ${monthly} monthly dues. These fund welfare, festivals, and operations, and any change must be approved at the Annual General Meeting.").replace("{reg}", reg).replace("{monthly}", monthly)],
    ["What happens if I fall behind on dues?", "Members who are 3 months in arrears are automatically placed out of good standing until dues are settled. You will receive reminder emails before general meetings."],
    ["Do donations count toward my dues?", "No. Gifts and donations are recorded separately with thanks and never reduce your balance due. You receive a year-end gift receipt by email for your records."],
    ["Who is eligible for welfare benefits?", "Members in good standing who have been members for at least one year are eligible for bereavement, sickness, and celebration benefits."],
    ["Can I pay dues online?", payAnswer],
    ["What is the 50% attendance rule?", "The constitution requires members to attend at least 50% of the year's general meetings (monthly General Meetings and the AGM) to remain in good standing. Board and committee meetings do not count toward the percentage."],
    ["Who do I contact with questions?", "Email info@okuapemmanma.org — replies to any email from the Association also reach the officers there."],
  ];
  return (
    <div data-testid="faq-page">
      <SubNav items={CONTACT_NAV} testId="contact-subnav" />
      <PageHero eyebrow="Questions" title="Frequently Asked Questions" />
      <Section>
        <Accordion type="single" collapsible className="mx-auto max-w-3xl">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`q${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-serif-display text-lg font-semibold text-crimson">{t(q)}</AccordionTrigger>
              <AccordionContent className="text-foreground/75">{t(a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
    </div>
  );
}

export function Contact() {
  const { t } = useLang();
  const { data: contact } = useApi("/settings/contact");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };
  return (
    <div data-testid="contact-page">
      <SubNav items={CONTACT_NAV} testId="contact-subnav" />
      <PageHero eyebrow="Reach Out" title="Contact Us"
        subtitle="Questions, welfare matters, or membership — we're here to help." />
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-4" data-testid="contact-form">
            <Input required placeholder={t("Your name")} value={form.name} data-testid="contact-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input required type="email" placeholder={t("Your email")} value={form.email} data-testid="contact-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input required placeholder={t("Subject")} value={form.subject} data-testid="contact-subject"
              onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <Textarea required placeholder={t("Your message")} rows={5} value={form.message} data-testid="contact-message"
              onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <Button type="submit" disabled={sending} className="w-full bg-crimson hover:bg-crimson-hover" data-testid="contact-submit">
              {sending ? t("Sending…") : t("Send message")}
            </Button>
          </form>
          <div className="space-y-4">
            <div className="rounded-2xl bg-palm p-8 text-parchment">
              <p className="font-serif-display text-3xl italic text-gold">Ofie! Biakoye!</p>
              <p className="mt-4 text-parchment/85" data-testid="contact-address">{contact?.address ?? "Worcester, Massachusetts"}</p>
              <p className="text-parchment/85" data-testid="contact-email">{contact?.email ?? "info@okuapemman-ma.org"}</p>
              <p className="text-parchment/85" data-testid="contact-phone">{contact?.phone ?? "+1 (617) 555-0100"}</p>{contact?.meeting_note && <p className="mt-3 text-sm text-parchment/70" data-testid="contact-note">{contact.meeting_note}</p>}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
